import { createColumnHelper } from '@tanstack/react-table';
import { FC, useCallback, useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { path } from '../../../paths';
import { UiLink } from '../../../paths/UiLink';
import translations from './DataSubjectTable.translations.json';
import {
  DataSubjectListDto,
  Table,
  TextEllipsis,
  getDatePeriodString,
  formatCodeName,
  DirectoryTableProps,
} from '@atlas-frontend/sdk';

const { addTranslations, useTranslation } = i18nNsFactory('data-subject-table');
addTranslations(translations);

const columnHelper = createColumnHelper<DataSubjectListDto>();

export type DataSubjectTableProps = DirectoryTableProps<DataSubjectListDto>;

export const DataSubjectTable: FC<DataSubjectTableProps> = (props) => {
  const { t } = useTranslation();

  const renderBoolean = useCallback(
    (value: boolean | null) =>
      value !== null ? (value ? t('value.yes') : t('value.no')) : '',
    [t],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('code', {
        header: t(`column.code`),
        cell: (props) => renderLink(props.renderValue(), props.row.original),
        enableColumnFilter: false,
        meta: {
          className: 'sticky-left',
          width: 110,
        },
      }),
      columnHelper.accessor('name', {
        header: t(`column.name`),
        cell: (props) => renderLink(props.renderValue(), props.row.original),
        enableColumnFilter: false,
        meta: {
          width: '40%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor((rowData) => formatCodeName(rowData.businessArea), {
        id: 'businessArea.name',
        header: t(`column.businessArea`),
        enableColumnFilter: false,
        meta: {
          width: '40%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor(
        (rowData) => formatCodeName(rowData.economicSector),
        {
          id: 'economicSector.name',
          header: t(`column.economicSector`),
          enableColumnFilter: false,
          meta: {
            width: '30ch',
          },
        },
      ),
      columnHelper.accessor((rowData) => formatCodeName(rowData.legalForm), {
        id: 'legalForm.name',
        header: t(`column.legalForm`),
        enableColumnFilter: false,
        meta: {
          width: '20ch',
        },
      }),
      columnHelper.accessor('validPeriod', {
        header: t(`column.validPeriod`),
        cell: (props) =>
          getDatePeriodString(
            props.renderValue()?.[0],
            props.renderValue()?.[1],
          ),
        enableColumnFilter: false,
        meta: {
          width: 180,
        },
      }),
      columnHelper.accessor('isFictitious', {
        header: t(`column.isFictitious`),
        cell: (props) => renderBoolean(props.renderValue()),
        enableColumnFilter: false,
        meta: {
          width: 95,
        },
      }),
      columnHelper.accessor('isPublic', {
        header: t(`column.isPublic`),
        cell: (props) => renderBoolean(props.renderValue()),
        enableColumnFilter: false,
        meta: {
          width: 80,
        },
      }),
      columnHelper.accessor('comment', {
        header: t(`column.comment`),
        enableColumnFilter: false,
        meta: {
          width: '20%',
          minWidth: 150,
        },
      }),
    ],
    [renderBoolean, t],
  );

  return (
    <Table
      name="data-subject-directory-table"
      columns={columns}
      hideClearFilters
      manualFiltering
      manualSorting
      scrollContainerSelector="#data-subject-directory-content"
      sticky
      virtual
      {...props}
    />
  );
};

const renderLink = (value: string | null, original: DataSubjectListDto) => {
  if (!value) return '';
  return (
    <TextEllipsis tooltip={value}>
      <UiLink
        id={`data-subject-code-${original.code}`}
        path={path.dataSubjectView}
        params={{ id: original.id }}
      >
        {value}
      </UiLink>
    </TextEllipsis>
  );
};
